import { useParams, Link } from "react-router-dom";
import { Clock, Check, ArrowRight, Phone } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import BookingForm from "@/components/BookingForm";
import { usePackage } from "@/hooks/usePublicData";
import { useSite, telLink, waLink } from "@/context/SiteContext";
import NotFound from "@/pages/NotFound";

export default function TourDetail() {
  const { slug } = useParams();
  const s = useSite();
  const { data: pkg, isLoading, isError } = usePackage(slug);

  if (isError) return <NotFound />;
  if (isLoading || !pkg) return <div className="container-x py-24 text-center text-slate-400">Loading…</div>;

  return (
    <>
      <Seo title={`${pkg.title} | Rajasthan Tour Package | Chouhan Taxi Service`}
        description={pkg.short} image={pkg.image} path={`/tour-packages/${slug}`} type="article" />
      <Breadcrumbs items={[{ label: "Tour Packages", to: "/tour-packages" }, { label: pkg.title }]} />
      <section className="relative h-72 sm:h-96">
        <img src={pkg.image} alt={pkg.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-navy/20" />
        <div className="relative container-x h-full flex flex-col justify-end pb-8">
          <div className="inline-flex items-center gap-1 bg-white/15 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full w-fit mb-3"><Clock className="w-3 h-3" /> {pkg.duration}</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-extrabold text-white">{pkg.title}</h1>
        </div>
      </section>
      <section className="section-pad pt-10">
        <div className="container-x grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <p className="text-lg text-slate-600 leading-relaxed">{pkg.description}</p>
            {pkg.highlights?.length > 0 && (
              <div className="mt-8">
                <h2 className="font-heading font-bold text-navy-heading text-xl mb-4">Tour Highlights</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {pkg.highlights.map((h) => (
                    <div key={h} className="flex items-start gap-3 bg-slate-50 rounded-xl p-4 border border-slate-100">
                      <Check className="w-5 h-5 text-brand shrink-0 mt-0.5" /><span className="text-sm text-slate-700">{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-8 bg-navy rounded-2xl p-6 text-white">
              <h3 className="font-heading font-bold text-lg mb-2">Whats included</h3>
              <p className="text-slate-300 text-sm leading-relaxed">A comfortable air-conditioned vehicle, professional driver, fuel and all road tolls & taxes. Accommodation, monument entry fees and meals can be arranged on request. Contact us for a custom quote based on your group size and preferences.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href={telLink(s)} className="inline-flex items-center gap-2 bg-white text-navy font-semibold px-5 py-2.5 rounded-full text-sm"><Phone className="w-4 h-4" /> {s.phone}</a>
                <a href={waLink(s, `Hi, I'm interested in the ${pkg.title} package.`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-whatsapp text-white font-semibold px-5 py-2.5 rounded-full text-sm"><WhatsAppIcon className="w-4 h-4" /> Enquire on WhatsApp</a>
              </div>
            </div>
          </div>
          <div className="lg:col-span-1">
            <div className="sticky top-28 bg-white rounded-2xl border border-slate-100 shadow-lg p-6">
              <h2 className="font-heading font-bold text-navy-heading text-lg mb-4">Enquire About This Tour</h2>
              <BookingForm compact defaults={{ trip_type: "Outstation", message: `Tour package enquiry: ${pkg.title}` }} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
